import { Button as MUIButton, styled } from '@mui/material'
import pallete from '../../../styles/colors'
import { forwardRef } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'

type ButtonVariant = 'primary' | 'secondary' | 'text' | 'danger'
export interface ButtonProps {
    label?: string
    variant?: ButtonVariant
    disabled?: boolean
    startIcon?: React.ReactNode
    endIcon?: React.ReactNode
    className?: string
    size?: 'medium' | 'small'
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    autoFocus?: boolean
    style?: React.CSSProperties
}

const VARIANT_CONFIGURATION = {
    primary: {
        backgroundColor: pallete.secondary.main,
        '&:hover': {
            backgroundColor: pallete.secondary.contrast,
        },
        '&:focus': {
            borderRadius: '4px',
            outline: `1px solid ${pallete.customtext.darkblue100}`,
        },
    },
    secondary: {
        color: pallete.customtext.darkblue100,
        backgroundColor: pallete.white,
        '&:hover': {
            backgroundColor: pallete.secondary1.grey,
        },
        '&:focus': {
            outline: `1px solid ${pallete.customtext.darkblue100}`,
            borderRadius: '4px',
        },
    },
    danger: {
        backgroundColor: tokens.ColorSurfaceDangerBoldDefault,
        '&:hover': {
            backgroundColor: tokens.ColorSurfaceDangerBoldHover,
        },
    },
    text: {
        justifyContent: 'initial',
        color: pallete.customtext.darkblue100,
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
        '&:focus': {
            textDecoration: 'underline',
        },
        textTransform: 'uppercase',
        fontWeight: 500,
    },
}

const SIZE_CONFIGURATION = {
    medium: {
        height: '40px',
        fontSize: '14px',
        '& .MuiButton-startIcon > *:nth-of-type(1)': {
            fontSize: '20px',
        },
        width: 'fit-content',
    },
    small: {
        height: '32px',
        fontSize: '11px',
        '& .MuiButton-startIcon > *:nth-of-type(1)': {
            fontSize: '16px',
            height: '16px',
            width: '16px',
        },
    },
}

const PADDING_CONFIGURATION = {
    small: {
        labelOnly: {
            padding: '0 16px',
        },
        iconOnly: {
            padding: '0 8px',
        },
        labelWithIcon: {
            padding: '0 16px 0 12px',
            '& .MuiButton-startIcon': {
                paddingRight: '6px',
            },
        },
    },
    medium: {
        labelOnly: {
            padding: '0 24px',
        },
        iconOnly: {
            padding: '0 10px',
        },
        labelWithIcon: {
            padding: '0 24px 0 16px',
            '& .MuiButton-startIcon': {
                paddingRight: '8px',
            },
        },
    },
}

const StyledButton = styled(MUIButton)({
    '& > .MuiButton-startIcon': {
        margin: 0,
    },
    minWidth: 0,
    lineHeight: '1.1',
})

/**
 * Supports passing WAI-ARIA attributes by spreading excess props
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            label,
            variant = 'primary',
            disabled = false,
            startIcon,
            endIcon,
            className,
            size = 'medium',
            onClick,
            autoFocus = false,
            ...props
        }: ButtonProps,
        ref
    ) => {
        let type: 'labelOnly' | 'iconOnly' | 'labelWithIcon' = 'labelOnly'
        if (label && startIcon) {
            type = 'labelWithIcon'
        } else if (startIcon) {
            type = 'iconOnly'
        }

        return (
            <StyledButton
                ref={ref}
                variant={variant === 'text' ? variant : 'contained'}
                autoFocus={autoFocus}
                disabled={disabled}
                disableRipple
                disableElevation
                sx={{
                    ...VARIANT_CONFIGURATION[variant],
                    ...SIZE_CONFIGURATION[size],
                    ...PADDING_CONFIGURATION[size][type],
                }}
                startIcon={startIcon}
                endIcon={endIcon}
                className={className}
                onClick={onClick}
                {...props}
            >
                {label}
            </StyledButton>
        )
    }
)
